import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';

/**
 * Who runs an event: the owner stated on the event row (`owner_kind` / `owner_id`, Members phase 5),
 * not the location it is played at. Mirrors the mobile RPC's `host_name` / `host_kind` / `host_id`.
 */
export type EventHostKind = 'club' | 'federation';
export type EventHost = { id: string; name: string; kind: EventHostKind };

type Client = SupabaseClient<Database>;
type OwnerFields = { owner_kind: string | null; owner_id: string | null; federation_id: string };

async function readName(
  client: Client,
  table: 'location' | 'federation',
  id: string,
): Promise<string | null> {
  const { data, error } = await client.from(table).select('name').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { name: string } | null)?.name ?? null;
}

/**
 * The event's host: the owner's `location.name` (club) or `federation.name`. Only a *missing* row
 * (no owner stated, or a dangling owner id) falls back to the event's own federation; when that has
 * no row either, `fallbackName` (the location name the page used before owners were read) stands in
 * as the federation, and with none the answer is `null`. A read *error* is never a fallback: it
 * throws, so a transient blip on a club-owned event is not cached as "Hosted by {federation}"
 * (callers degrade to their uncached path, or to the location as the host, and report).
 */
export async function readEventHost(
  client: Client,
  event: OwnerFields,
  fallbackName: string | null,
): Promise<EventHost | null> {
  const kind: EventHostKind | null =
    event.owner_kind === 'club' || event.owner_kind === 'federation' ? event.owner_kind : null;
  if (kind && event.owner_id) {
    const name = await readName(
      client,
      kind === 'club' ? 'location' : 'federation',
      event.owner_id,
    );
    if (name) return { id: event.owner_id, name, kind };
    // No row: a dangling owner id, read as the event's federation below.
  }
  const name = await readName(client, 'federation', event.federation_id);
  if (name) return { id: event.federation_id, name, kind: 'federation' };
  return fallbackName ? { id: event.federation_id, name: fallbackName, kind: 'federation' } : null;
}
